import {head, uniq} from "lodash";

import {parseDateTimestamp} from "@atg-shared/datetime";
import {noTrans} from "@atg-shared/no-trans";
import {GameType} from "@atg-tillsammans-shared/types";

import type {
    Offering,
    Pool as OfferingPool,
} from "@atg-sport-shared/pool-types/offeringTypes";
import type {HorseGame, Game, GamePool, GameSummary} from "@atg-tillsammans/game-types";

export const isSportGame = (gameId: string): boolean =>
    gameId.includes("BIG9") || gameId.includes("SP96");
export const isHorseGame = (gameId: string): boolean => !isSportGame(gameId);
export const getGameType = (gameId: string): GameType => gameId.split("_")[0] as GameType;
export const getGameDate = (gameId: string): string => gameId.split("_")[1];

export const mapHorseGameToGameSummary = (game: HorseGame): GameSummary => ({
    id: game.id,
    type: getGameType(game.id),
    pool: Object.values(game.pools ?? []).reduce<GamePool | undefined>(
        (acc, pool) =>
            pool.id !== game.id
                ? acc
                : {
                      timestamp: pool.timestamp,
                      turnover: pool.turnover,
                      systemCount: pool.systemCount,
                      payouts: pool.payouts,
                  },
        undefined,
    ),
});

export const mapSportGameToGameSummary = (offering: Offering): GameSummary => {
    function getPayouts(
        pool: OfferingPool,
        gameType: GameType,
    ): {[key: string]: number} | undefined {
        if (!pool.payouts) return undefined;

        const payoutsKeys: Partial<Record<GameType, string[]>> = {
            [GameType.BIG9]: ["9", "8", "7"],
            [GameType.SP96]: ["9", "8", "7", "6"],
        };

        const keys = payoutsKeys[gameType];

        if (!keys) return undefined;

        const payoutsByCorrectCount = pool.payouts as Record<
            string,
            {pot: number} | undefined
        >;

        return Object.fromEntries(
            keys.map((key) => [key, payoutsByCorrectCount[key]?.pot ?? 0]),
        );
    }

    const gameType = getGameType(offering.id);

    return {
        id: offering.id,
        type: gameType,
        pool: {
            timestamp: offering.pool.timestamp,
            turnover: offering.pool.turnover,
            systemCount: Math.round(offering.pool.numberOfSystems),
            payouts: getPayouts(offering.pool, gameType),
        },
    };
};

export const mapGameToGameSummary = (game: Game): GameSummary =>
    isSportGame(game.id)
        ? mapSportGameToGameSummary(game as Offering)
        : mapHorseGameToGameSummary(game as HorseGame);

export const getGameInfo = (game: HorseGame) => {
    const startTime = head(game.races)?.startTime;
    const formattedStartTime = startTime
        ? parseDateTimestamp(startTime).format(noTrans("dddd D MMM, [kl] LT"))
        : "-";

    return {
        gameType: game.type,
        gameStartTime:
            formattedStartTime !== "-"
                ? `${
                      formattedStartTime.charAt(0).toUpperCase() +
                      formattedStartTime.slice(1)
                  }`
                : formattedStartTime,

        gameTracks: uniq(game.races.map((race) => race.track.name)).join(" & "),
    };
};
